import { useState, useCallback, useEffect, useRef } from 'react';
import { MessageSquarePlus, X } from 'lucide-react';
import type { Comment } from '@/types';

interface SelectionPosition {
  top: number;
  left: number;
}

export function useTextSelection(
  containerRef: React.RefObject<HTMLDivElement>,
  existingComments: Comment[]
) {
  const [selection, setSelection] = useState<{
    text: string;
    startOffset: number;
    endOffset: number;
    position: SelectionPosition | null;
  } | null>(null);
  const [highlightedRanges, setHighlightedRanges] = useState<Comment[]>(existingComments);

  useEffect(() => {
    setHighlightedRanges(existingComments);
  }, [existingComments]);

  const handleMouseUp = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const selectionObj = window.getSelection();
    if (!selectionObj || selectionObj.isCollapsed) {
      setSelection(null);
      return;
    }

    const text = selectionObj.toString().trim();
    if (!text) {
      setSelection(null);
      return;
    }

    // Check if selection is within the container
    const range = selectionObj.getRangeAt(0);
    if (!container.contains(range.commonAncestorContainer)) {
      setSelection(null);
      return;
    }

    // Calculate the global offset within the container
    const preSelectionRange = document.createRange();
    preSelectionRange.selectNodeContents(container);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    const startOffset = preSelectionRange.toString().length;
    const endOffset = startOffset + text.length;

    // Get position for the tooltip
    const rect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const position: SelectionPosition = {
      top: rect.top - containerRect.top - 40,
      left: rect.left - containerRect.left + rect.width / 2,
    };

    setSelection({
      text,
      startOffset,
      endOffset,
      position,
    });
  }, [containerRef]);

  const clearSelection = useCallback(() => {
    setSelection(null);
    const selectionObj = window.getSelection();
    if (selectionObj) {
      selectionObj.removeAllRanges();
    }
  }, []);

  return {
    selection,
    clearSelection,
    handleMouseUp,
    highlightedRanges,
  };
}

interface SelectionTooltipProps {
  selection: {
    text: string;
    startOffset: number;
    endOffset: number;
    position: SelectionPosition | null;
  };
  onAddComment: () => void;
  onDismiss: () => void;
}

export function SelectionTooltip({
  selection,
  onAddComment,
  onDismiss,
}: SelectionTooltipProps) {
  if (!selection.position) return null;

  return (
    <div
      className="absolute z-50 flex items-center space-x-1 bg-primary text-primary-foreground px-2 py-1 rounded-lg shadow-lg"
      style={{
        top: selection.position.top,
        left: selection.position.left,
        transform: 'translateX(-50%)',
      }}
    >
      <button
        onClick={onAddComment}
        className="flex items-center space-x-1 px-2 py-1 hover:bg-primary/80 rounded text-sm"
      >
        <MessageSquarePlus className="h-4 w-4" />
        <span>添加评论</span>
      </button>
      <button
        onClick={onDismiss}
        className="p-1 hover:bg-primary/80 rounded"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

interface CommentInputModalProps {
  isOpen: boolean;
  selectedText: string;
  onClose: () => void;
  onSubmit: (content: string) => Promise<void>;
}

export function CommentInputModal({
  isOpen,
  selectedText,
  onClose,
  onSubmit,
}: CommentInputModalProps) {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setContent('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setIsSubmitting(true);
    try {
      await onSubmit(content.trim());
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-medium">添加评论</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Selected text */}
        <div className="px-4 py-3 border-b bg-muted/30">
          <div className="text-xs text-muted-foreground mb-1">选中文本:</div>
          <div className="text-sm bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2 rounded border-l-4 border-yellow-400 italic">
            "{selectedText.length > 200 ? selectedText.slice(0, 200) + '...' : selectedText}"
          </div>
        </div>

        {/* Comment input */}
        <div className="p-4">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="输入评论内容..."
            className="w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            rows={4}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 px-4 py-3 border-t bg-muted/30">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm hover:bg-muted rounded-lg"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !content.trim()}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {isSubmitting ? '提交中...' : '提交评论'}
          </button>
        </div>
      </div>
    </div>
  );
}
